/**
 * scenes/chat/townChat — the pure decisions behind the Town chat panel's two-way
 * actions, factored out of {@link import('../TownScene').TownScene} so they can be
 * unit-tested without a Phaser scene.
 *
 * The renderer owns no rules: TownScene only echoes the player's line + drives the
 * stream; WHICH mon is shown off and HOW the player's line reads are decided here,
 * and the actual ground-truth beats are minted by society/ + chat-director/.
 */
import type { PokemonInstance } from '@/shared';

/**
 * Pick the Pokémon the player shows off: the follower if set, else the team lead,
 * else null (empty team). Mirrors {@link import('@/session').GameSession.showOffInChat}
 * so the echoed line + the posted beat always agree on the same star.
 */
export function showOffTarget(
  follower: PokemonInstance | null,
  team: readonly PokemonInstance[],
): PokemonInstance | null {
  return follower ?? team[0] ?? null;
}

/** A friendly display name for a mon: its nickname, else the species label. */
export function monLabel(
  mon: PokemonInstance,
  speciesLabel: (speciesId: string) => string,
): string {
  return mon.nickname ?? speciesLabel(mon.speciesId);
}

/** The warm line the player's avatar emits when peacocking their lead. */
export function showOffEchoLine(
  mon: PokemonInstance,
  speciesLabel: (speciesId: string) => string,
): string {
  return `Check out my ${monLabel(mon, speciesLabel)}! ✨`;
}

/** Whether the "Show off" quick reply should appear at all (only with a team). */
export function canShowOff(
  follower: PokemonInstance | null,
  team: readonly PokemonInstance[],
): boolean {
  return showOffTarget(follower, team) !== null;
}
